import axios from 'axios';
import { Reminder } from './reminderService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types
export interface DashboardData {
  userInfo: UserDashboardInfo;
  remindersSummary: RemindersSummary;
  salesRepData?: SalesRepDashboardData;
  caseManagerData?: CaseManagerDashboardData;
  systemAdminData?: SystemAdminDashboardData;
}

export interface UserDashboardInfo {
  id: number;
  fullName: string;
  email: string;
  permissionLevel: number;
  permissionLevelName: string;
  isSupervisor: boolean;
}

export interface RemindersSummary {
  todayCount: number;
  overdueCount: number;
  upcomingCount: number;
  todayReminders: Reminder[];
  overdueReminders: Reminder[];
  upcomingReminders: Reminder[];
}

export interface SalesRepDashboardData {
  performance: MonthlyPerformance;
  pipeline: LeadsPipelineSummary;
  newLeadsCount: number;
  awaitingDecisionCount: number;
  signedThisMonthCount: number;
  teamOverview?: TeamOverview; // Only populated for supervisors
  leadStatuses: LeadStatusSummary[]; // Dynamic status counts
  commissionsOverview: CommissionsOverview; // עמלות הנציג (שלו בלבד; מפקח מלא — כל הנציגים)
}

export interface LeadStatusSummary {
  statusId: number;
  statusName: string;
  statusColor: string;
  count: number;
  teamCount: number; // For supervisors - team-wide count
}

export interface MonthlyPerformance {
  newLeadsReceived: number;
  signatures: number;
  conversionRate: number;
  teamRanking: number;
  totalTeamMembers: number;
}

export interface LeadsPipelineSummary {
  redStatusCount: number;
  orangeStatusCount: number;
  greenStatusCount: number;
  totalActiveLeads: number;
}

export interface TeamOverview {
  totalTeamLeads: number;
  unhandledLeadsCount: number;
  teamPipeline: LeadsPipelineSummary;
  teamSignaturesThisMonth: number;
  teamConversionRate: number;
  teamPerformance: MonthlyPerformance;
  teamNewLeadsCount: number;
  teamAwaitingDecisionCount: number;
}

export interface CaseManagerDashboardData {
  activeCasesCount: number;
  pendingDocumentsCount: number;
  upcomingCommitteesCount: number;
  completedThisMonthCount: number;
}

export interface SystemAdminDashboardData {
  systemStats: SystemStats;
  recentUserActivity: UserActivitySummary[];
  pendingApprovalsCount: number;
  systemPerformance: MonthlyPerformance;
  systemPipeline: LeadsPipelineSummary;
  systemNewLeadsCount: number;
  systemAwaitingDecisionCount: number;
  systemSignedThisMonthCount: number;
  leadStatuses: LeadStatusSummary[]; // Dynamic status counts for admin

  // New enhanced metrics
  financialOverview: FinancialOverview;
  casesMetrics: CasesMetrics;
  topSalesReps: TopSalesRep[];
  topCaseManagers: TopCaseManager[];
  cashFlowTrend: CashFlowTrend[];
  urgentTasksSummary: AlertsSummary;

  // Status distributions
  leadStatusDistribution: StatusDistribution[];
  clientStatusDistribution: StatusDistribution[];

  // Special Payments Summary
  specialPaymentsSummary: SpecialPaymentsSummary;

  // עמלות נציגי מכירות
  commissionsOverview: CommissionsOverview;
}

// עמלות נציגי מכירות — חישוב on-the-fly: עמלה = קבוע + (אחוז/100) × סכום החיוב
export interface CommissionsOverview {
  totalCommissions: number;
  reps: SalesRepCommission[];
}

export interface SalesRepCommission {
  salesRepId: number;
  salesRepName: string;
  totalCommission: number;
  clients: ClientCommission[];
}

export interface ClientCommission {
  clientId: number;
  clientName: string;
  totalCharge: number;
  commissionPercentage: number;
  commissionFixedAmount: number;
  commission: number;
}

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalLeads: number;
  totalClients: number;
  totalCases: number;
}

export interface UserActivitySummary {
  userId: number;
  userName: string;
  lastActivityType: string;
  lastActivityTime: string;
}

// Financial Overview
export interface FinancialOverview {
  actualIncomeThisMonth: number;
  actualIncomeLastMonth: number;
  actualIncomePercentageChange: number;
  expectedIncomeNextMonth: number;
  expectedIncomePercentageChange: number;
  openDebtsBalance: number;
  openDebtsPercentageChange: number;
  activePaymentPlansCount: number;
  overduePaymentPlansCount: number;
}

// Cases Metrics
export interface CasesMetrics {
  activeCasesCount: number;
  committeesThisMonth: number;
  committeesThisWeek: number;
  approvedCasesThisMonth: number;
  rejectedCasesThisMonth: number;
  totalMonthlyBenefitsAchieved: number;
  totalRetroactiveBenefitsAchieved: number;
}

// Top Performers
export interface TopSalesRep {
  userId: number;
  name: string;
  leadsCount: number;
  signaturesCount: number;
  conversionRate: number;
  ranking: number;
}

export interface TopCaseManager {
  userId: number;
  name: string;
  casesManaged: number;
  approvedCases: number;
  totalMonthlyBenefits: number;
  expectedIncome: number;
}

// Cash Flow Trend
export interface CashFlowTrend {
  monthOffset: number;
  monthName: string;
  monthDate: string;
  actualAmount: number;
  forecastAmount: number;
  isForecast: boolean;
}

// Alerts Summary
export interface AlertsSummary {
  coldLeadsCount: number;
  overduePaymentsCount: number;
  upcomingCommitteesCount: number;
  pendingDocumentsCount: number;
}

// Status Distribution
export interface StatusDistribution {
  statusId: number;
  statusName: string;
  statusColor: string;
  count: number;
  percentage: number;
}

// API calls
const dashboardApi = {
  getDashboardData: async (): Promise<DashboardData> => {
    const response = await axios.get(`${API_BASE_URL}/dashboard`);
    return response.data;
  }
};

// Special Payments Summary
export interface SpecialPaymentsSummary {
  activePaymentsCount: number;
  activePaymentsAmount: number;
  paidThisMonthCount: number;
  paidThisMonthAmount: number;
  totalEverPaid: number;
  uniqueClientsCount: number;
}

// Helper functions
export const getPipelineStatusColor = (status: 'red' | 'orange' | 'green'): string => {
  switch (status) {
    case 'red': return 'var(--error-color)';
    case 'orange': return 'var(--warning-color)';
    case 'green': return 'var(--success-color)';
    default: return 'var(--text-secondary)';
  }
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';

export default dashboardApi;